import { useState, useEffect, useRef } from 'react';

import './ConversationProfil.css';
import ConversationHeader from './ConversationHeader/ConversationHeader';
import ConversationPlayer from './ConversationPlayer/ConversationPlayer';
import ConversationCard from './ConversationCard/ConversationCard';
import ConversationCompletedService from '/src/features/supervision/conversations/services/ConversationCompletedService';
import { useNavigate, useParams } from "react-router-dom";
import FloatingMenu from '../FloatingMenu/FloatingMenu';
import ConversationToggleTranslate from "./ConversationToggleTranslate/ConversationToggleTranslate"
import { useMode } from './ConversationCard/ModeContext/ModeContext';
import PresenceWarningBanner from './PresenceWarningBanner/PresenceWarningBanner';
import ReviewTimer from '../ReviewTimer/ReviewTimer';
import storage from '/src/utils/storage';
import hasPermission from '/src/utils/hasPermission';

function ConversationProfil() {
    const navigate = useNavigate();
    const { id } = useParams();
    const audioRef = useRef(null);
    // Page entry time: same moment the heartbeat below starts the backend's review clock.
    const enteredAtRef = useRef(Date.now());
    const floatingMenuRef = useRef(null);
    const currentUser = storage("local").get("user");

    const { getConversation, translateConversation, translateConversationStatus, sendPresenceHeartbeat, leavePresence, leavePresenceOnUnload, getConversationsPresence } = ConversationCompletedService;
    const { mode, setMode } = useMode();

    const canViewReviewing = currentUser?.is_admin || hasPermission('view_reviewing');

    const [presentUsers, setPresentUsers] = useState([]);
    const [dismissedPresenceKey, setDismissedPresenceKey] = useState("");
    const presenceKey = presentUsers.map((u) => u.email).sort().join(",");
    const showPresenceWarning = canViewReviewing && presentUsers.length > 0 && dismissedPresenceKey !== presenceKey;

    const [conversation, setConversation] = useState(null);
    const isEnglish = /-en(?:-|$)/.test(conversation?.data?.unit_id ?? "");
    const canTranslateConversation = currentUser?.is_admin || hasPermission('translate_conversation');
    const shouldShowTranslate = !isEnglish && canTranslateConversation;
    const [currentTime, setCurrentTime] = useState(0);
    const pollingRef = useRef(null);
    const [isTranslating, setIsTranslating] = useState(false);

    const splitConversationBySpeaker = (content = []) =>
        content?.reduce(
            (acc, { speaker, ...rest }, segment_index) => {
                const segment = { ...rest, segment_index };
                if (speaker === 1) {
                    acc.right.push(segment);
                } else {
                    acc.left.push(segment);
                }
                return acc;
            },
            { left: [], right: [] }
        );

    useEffect(() => {
        return () => {
            setMode("original");
        };
    }, [setMode]);

    useEffect(() => {
        enteredAtRef.current = Date.now();
        sendPresenceHeartbeat(id);
        const heartbeatInterval = setInterval(() => sendPresenceHeartbeat(id), 15000);
        const handlePageHide = () => leavePresenceOnUnload(id);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            clearInterval(heartbeatInterval);
            leavePresence(id);
        };
    }, [id]);

    useEffect(() => {
        // The heartbeat above always announces this user's presence (so
        // compliance can still see them) - this is only about whether *this*
        // user is allowed to see who else is reviewing, so skip the fetch
        // entirely (and the warning banner with it) if they can't.
        if (!canViewReviewing) return;

        const fetchPresence = async () => {
            const [response, error] = await getConversationsPresence([id]);
            if (error || !response) return;

            const others = (response.data?.[id] || []).filter((u) => u.email !== currentUser?.email);
            setPresentUsers(others);
        };

        fetchPresence();
        const interval = setInterval(fetchPresence, 15000);
        return () => clearInterval(interval);
    }, [id, canViewReviewing]);

    useEffect(() => {

        const fetchData = async () => {
            try {
                const [response, error] = await getConversation({ id });

                // The backend returns null both when the conversation doesn't
                // exist and when the current user isn't allowed to see it
                // (unit/own-conversations scoping) - either way, bounce back
                // to the list instead of leaving the page stuck on "Loading…".
                if (error || !response?.data) {
                    navigate("/supervision/conversations/list");
                    return;
                }

                response.data.content = splitConversationBySpeaker(response.data.content);
                response.data.translate_content = splitConversationBySpeaker(response.data.translate_content);

                setConversation(response);
            } catch (e) {
                console.error(e);
            }
        };

        fetchData();
    }, [id, setMode]);


    useEffect(() => {
        if (mode !== "translated") {
            setIsTranslating(false);
            return;
        }

        const launchTranslation = async () => {
            setIsTranslating(true);

            const [response, error] = await translateConversation({ call_id: id });

            if (error) {
                setIsTranslating(false);
                console.error(error);
                return;
            }

            pollingRef.current = setInterval(async () => {
                const [statusResponse, statusError] = await translateConversationStatus({ call_id: id });

                if (statusError) {
                    console.error(statusError);
                    return;
                }

                if (statusResponse.status === "done") {
                    clearInterval(pollingRef.current);

                    setConversation(prev => ({
                        ...prev,
                        data: {
                            ...prev.data,
                            translate_content: splitConversationBySpeaker(statusResponse.translation)
                        }
                    }));

                    setIsTranslating(false);
                }
            }, 1000);
        };

        launchTranslation();

        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, [mode, id]);

    if (!conversation) return <div>Loading…</div>;

    return (
        <>
            {showPresenceWarning && (
                <PresenceWarningBanner
                    users={presentUsers}
                    onDismiss={() => setDismissedPresenceKey(presenceKey)}
                />
            )}
            <div className="conversation-profil">
                <ConversationHeader conversation={conversation.data} setConversation={setConversation} presentUsers={presentUsers} />
                {
                    shouldShowTranslate &&

                    <div className="top_layer">
                        <ConversationToggleTranslate isLoading={isTranslating} />
                    </div>
                }
                <br></br> <br></br>
                <ConversationPlayer conversation={conversation.data} audioRef={audioRef} setCurrentTimeAudio={setCurrentTime} />
                <br></br> <br></br>
                <ConversationCard
                    data={conversation}
                    audioRef={audioRef}
                    setConversationData={setConversation}
                    onNavigateToTimestamp={(start) => floatingMenuRef.current?.openTranscriptionAt(start)}
                />
                <ReviewTimer key={id} startedAt={enteredAtRef.current} />
                <FloatingMenu ref={floatingMenuRef} conversation={conversation} setConversation={setConversation} audioRef={audioRef} currentTime={currentTime} />
            </div>
        </>
    );
}

export default ConversationProfil;